import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * A panel is the only elevated container in this app, and it means "this is a
 * distinct region of the page". Grouping *inside* a panel is done with
 * divide-y, border-t and whitespace, never with nested panels.
 */
export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn("rounded-xl border border-line bg-surface", className)}>{children}</section>;
}

export function PanelHeader({
  title,
  note,
  actions,
  className,
}: {
  title: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3",
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-sm font-semibold tracking-tight text-ink">{title}</h2>
        {note && <p className="mt-0.5 text-xs leading-normal text-muted">{note}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}

export function PanelBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("p-4", className)}>{children}</div>;
}

/** Page-level section heading, for grouping panels under a common idea. */
export function SectionHeading({
  title,
  note,
  actions,
}: {
  title: string;
  note?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-ink">{title}</h2>
        {note && <p className="mt-1 max-w-[65ch] text-sm leading-relaxed text-muted">{note}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
